import * as React from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { formatAmount } from "@/lib/format"
import { t } from "@/lib/i18n"
import { reportWriteError } from "@/lib/writeErrors"
import { nombreCuenta, nombrePais } from "@/routes/finance/mapping"

import {
  borrarAjuste,
  guardarAjuste,
  type BloqueAjustable,
  type EscenarioDelAjuste,
  type IdEscenario,
} from "@/routes/forecast/api"

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
               "agosto", "septiembre", "octubre", "noviembre", "diciembre"]

export interface CeldaProyectada {
  pais: string
  anio: number
  mes: number
  bloque: BloqueAjustable
  cuenta: string
  /** Lo que la proyección da hoy para esta celda. */
  proyectado: number
  /** El ajuste que ya tiene, si lo tiene. */
  ajuste?: { valor_usd: number; motivo: string; escenario: EscenarioDelAjuste } | null
  /** El escenario que se está mirando, para preseleccionarlo. */
  escenarioActual: IdEscenario
}

/**
 * PISAR UNA CELDA DEL FORECAST.
 *
 * Punto 6 de Cesar, segunda mitad: «poder pisar una celda concreta».
 *
 * ---------------------------------------------------------------------------
 * POR DEFECTO SE APLICA A LOS TRES ESCENARIOS, Y NO ES PEREZA
 *
 * Un ajuste es un HECHO CONOCIDO —«en septiembre entra el contrato de
 * Falabella»—, y un hecho no cambia porque el mes sea bueno o malo. Obligar a
 * teclearlo tres veces garantiza que acaben desincronizados: alguien corrige
 * el moderado en abril y el pesimista se queda diciendo lo de marzo.
 *
 * Quien de verdad quiera un valor distinto por escenario lo dice cambiando el
 * selector, y entonces ese ajuste gana sobre el de «los tres».
 *
 * ---------------------------------------------------------------------------
 * EL AJUSTE NO SE ESCALA CON EL ESCENARIO
 *
 * Se dice en el diálogo porque es lo primero que alguien espera al revés: si
 * escribes 40.000 en el pesimista, el pesimista dice 40.000 y no 32.000. Quien
 * escribió el número ya sabía en qué escenario estaba.
 */
export function AjustarCeldaDialog({
  celda, open, onOpenChange,
}: {
  celda: CeldaProyectada | null
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [valor, setValor] = React.useState("")
  const [motivo, setMotivo] = React.useState("")
  const [alcance, setAlcance] = React.useState<EscenarioDelAjuste>("todos")

  React.useEffect(() => {
    if (!celda) return
    setValor(String(celda.ajuste?.valor_usd ?? Math.round(celda.proyectado)))
    setMotivo(celda.ajuste?.motivo ?? "")
    setAlcance(celda.ajuste?.escenario ?? "todos")
  }, [celda])

  function invalidar() {
    for (const k of ["forecast-proyeccion", "forecast-caja", "forecast-cobranza",
                     "forecast-ajustes"]) {
      void queryClient.invalidateQueries({ queryKey: [k] })
    }
  }

  const guardar = useMutation({
    mutationFn: () =>
      guardarAjuste({
        pais: celda!.pais, anio: celda!.anio, mes: celda!.mes,
        escenario: alcance, bloque: celda!.bloque, cuenta: celda!.cuenta,
        valor_usd: Number(valor.replace(",", ".")),
        motivo,
        // LO QUE DABA ANTES. Se manda desde aquí porque la pantalla ya lo
        // tiene delante; el servidor lo guarda para poder decir «antes decía
        // X» y para que el consolidado sepa cuánto restar.
        revierte_a: celda!.ajuste ? undefined : celda!.proyectado,
      }),
    onSuccess: () => {
      invalidar()
      toast({
        title: t("Ajuste guardado"),
        description: t("No se vuelve a escalar con el escenario: es un hecho que tú conoces, no una consecuencia del factor."),
      })
      onOpenChange(false)
    },
    onError: (e) => reportWriteError(e, { entidad: "el ajuste", operacion: "guardar" }),
  })

  const quitar = useMutation({
    mutationFn: () =>
      borrarAjuste({
        pais: celda!.pais, anio: celda!.anio, mes: celda!.mes,
        escenario: celda!.ajuste!.escenario, bloque: celda!.bloque,
        cuenta: celda!.cuenta,
      }),
    onSuccess: () => {
      invalidar()
      toast({ title: t("Ajuste quitado"),
              description: t("La celda vuelve a salir del escenario.") })
      onOpenChange(false)
    },
    onError: (e) => reportWriteError(e, { entidad: "el ajuste", operacion: "quitar" }),
  })

  const numero = Number(valor.replace(",", "."))
  const puedeGuardar = Number.isFinite(numero) && motivo.trim().length >= 3

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {celda ? `${nombreCuenta(celda.cuenta)} · ${MESES[celda.mes - 1]} ${celda.anio}` : ""}
          </DialogTitle>
          <DialogDescription>
            {celda ? `${nombrePais(celda.pais)} · ` : ""}
            {t("Escribe lo que sabes que va a pasar. Marty deja de calcular esta celda y usa tu número.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md bg-surface-sunken/60 px-2.5 py-2 text-2xs">
            <span className="text-muted-foreground">{t("La proyección da")}</span>{" "}
            <span className="num-strong">{formatAmount(celda?.proyectado ?? 0)}</span>
            {celda?.ajuste ? (
              <>
                {" · "}
                <span className="text-muted-foreground">{t("ahora dice")}</span>{" "}
                <span className="num-strong">{formatAmount(celda.ajuste.valor_usd)}</span>
              </>
            ) : null}
          </div>

          <div className="space-y-1">
            <Label htmlFor="ajuste-valor">{t("Cuánto, en USD")}</Label>
            <Input
              id="ajuste-valor"
              type="number"
              step="0.01"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="ajuste-alcance">{t("En qué escenarios")}</Label>
            <select
              id="ajuste-alcance"
              value={alcance}
              onChange={(e) => setAlcance(e.target.value as EscenarioDelAjuste)}
              className="h-9 w-full rounded-md border bg-surface px-2 text-sm"
            >
              <option value="todos">{t("En los tres (es un hecho, no depende del escenario)")}</option>
              <option value="pesimista">{t("Sólo en el pesimista")}</option>
              <option value="moderado">{t("Sólo en el moderado")}</option>
              <option value="optimista">{t("Sólo en el optimista")}</option>
            </select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="ajuste-motivo">{t("Por qué")}</Label>
            <Textarea
              id="ajuste-motivo"
              rows={2}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={t("Entra el contrato de Falabella, firmado el 12/09")}
            />
            {/*
              EL MOTIVO ES OBLIGATORIO. Una cifra proyectada que alguien cambió
              a mano y nadie sabe por qué es peor que la original, porque encima
              parece deliberada.
            */}
            <p className="text-2xs leading-4 text-muted-foreground">
              {t("Queda con tu nombre y la fecha, y se ve al pie de la proyección.")}
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {celda?.ajuste ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => quitar.mutate()}
              disabled={quitar.isPending}
            >
              {t("Quitar el ajuste")}
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("Cancelar")}
            </Button>
            <Button
              onClick={() => guardar.mutate()}
              disabled={!puedeGuardar || guardar.isPending}
            >
              {t("Guardar")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
